import BlogArticle from 'components/blog/BlogArticle';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { getCta } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/how-to-travel-sustainably-with-daymade`;

  return (
    <MainLayout>
      <NextSeo
        title="How to Travel Sustainably with DAYMADE | Win Epic Prizes | DAYMADE"
        description="Eco-friendly travel with minimal carbon footprint! Find out how to travel sustainably and reduce your carbon footprint through DAYMADE’s travel prizes."
        openGraph={{
          title:
            'How to Travel Sustainably with DAYMADE | Win Epic Prizes | DAYMADE',
          description:
            'Eco-friendly travel with minimal carbon footprint! Find out how to travel sustainably and reduce your carbon footprint through DAYMADE’s travel prizes.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/how-to-travel-sustainably-with-daymade',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A man standing on a rock in front of a waterfall',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'sustainable travel,eco travel,ecotourism,responsible travel,ethical travel,win a prize,win a holiday,win a trip,competition,carbon footprint,plant a tree,eco gift,much better adventures,luckytrip,trees for the future,C Level,ecologi,offset earth,carbon offsetting,reduce emissions,greenland,cornwall,wales,ireland,finn lough',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A man standing on a rock in front of a waterfall"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            How to Travel Sustainably with <strong>DAYMADE</strong>
          </h1>

          <p className="subheading">
            Eco-friendly travel with minimal carbon footprint
          </p>

          <div className="separator" style={{ width: '80%', marginTop: 0 }} />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            Travelling is without a doubt the best way to gain new perspectives,
            broaden your horizons and experience new cultures first-hand.
            Whether you’re motivated by the desire to escape a demanding job or
            you’re yearning for a cultural crash-course, travel offers us the
            opportunity to glimpse a different life from the one we’re living.
            But with 1.4 billion international arrivals per year, travel can
            come at an environmental and social cost. DAYMADE is all about
            experiences over money. But we also think travel could be done in a
            more eco-friendly and sustainable way. Here’s some ways in which
            DAYMADE attempts to make a difference.
          </p>

          <p>
            We work closely with Ecologi to ensure we give more back to the
            planet than we take. Our work with Ecologi funds projects that
            remove more greenhouse gases than our team’s carbon footprint. These
            projects range from carbon reduction through solar power generation
            in India to the planting of forest gardens in Uganda, each one
            offering vital greenhouse gas emission reduction.
          </p>

          <p>
            We love sending our winners around the world on life-changing trips
            and adventures, but we’re also aware of the environmental cost of
            flights. That’s why we’ve partnered with{' '}
            <a
              href="https://www.clevel.co.uk/"
              target="_blank"
              rel="noopener"
              style={{ textDecoration: 'underline' }}
            >
              C Level
            </a>{' '}
            to offset the carbon footprint of our flights, minimising our
            environmental impact. This carbon offsetting comes from a variety of
            projects promoting quantifiable climate protection and global
            sustainability. The projects we support vary from transforming waste
            into biogas in Vietnam to community reforestation in Nicaragua.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A woman and child planting native trees in Ethiopia"
            className="singleBlogImage"
          />

          <p>
            We’re proud to offset the carbon footprint of the DAYMADE team and
            our winners’ flights, but we wanted to go one step further and help
            to offset our players’ carbon footprint. Dedicated to doing right by
            our players and our planet, we choose to do our bit by planting a
            tree for every purchase. So far that’s already 290,000+ trees
            planted with Eden Reforestation Projects! These trees are integral
            in the work to protect and revitalise land, preventing flooding
            erosion and landslides. They also greatly support biodiversity, not
            to mention the livelihoods of local farmers and communities for
            generations to come. Eden plant Forest Gardens, which consist of a
            variety of plants ranging from trees to shrubs, fruits to
            vegetables. This diverse array of species allows a farming family to
            use agroforestry to sustainably build their income, nutrition and
            way of life for years to come. Afterall, sustainability shouldn’t
            just be focussed on the fight against climate change – we should
            work to ensure we maintain our environment and natural habitats in
            the long-term.
          </p>

          <p>
            We’re proud to have partnered with the travel agency Much Better
            Adventures, who aim to create the world’s most positive-impact
            adventure company. They’re all about providing amazing outdoor
            experiences across the globe that are sustainable and eco-friendly -
            a goal we’re 1000% in support of!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A woman walking with her three cattle in Myanmar"
            className="singleBlogImage"
          />

          <p>
            Local communities can be greatly affected by tourism, both
            positively and negatively. It is therefore essential that local
            communities be sustainably supported and not exploited. Much Better
            Adventures work with 100% locally-owned independent businesses, with
            an average of 80% of money spent staying in the local economy. This
            allows local communities to be empowered through sustainable
            business practices and work towards local nature conservation
            efforts. And with their pledge to plant enough native trees to suck
            twice as much carbon back out of the atmosphere as the amount
            produced for each trip, it’s easy to see how our partnership
            together has formed.
          </p>

          <p>
            We offer a variety of trips with Much Better Adventures as prizes
            across our £300, £2,000 and £10,000 prize categories. You can read
            more about these amazing getaways in further detail {''}
            <Link href="/blog/daymade-x-much-better-adventures">
              <a
                style={{
                  textDecoration: 'underline',
                  whiteSpace: 'break-spaces',
                }}
              >
                here
              </a>
            </Link>
            .
          </p>

          <p>
            We’re proud to offer a variety of eco-friendly travel prizes with
            our partners, so take a look at some of our favourites below.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Person kayaking on a body of water past Vatnajökull glacier in Jökulsárlón, Iceland"
            className="singleBlogImage"
          />

          <p>
            If exploring unspoilt wilderness by water and on foot makes your
            heart skip a beat, this one’s for you. Set sail on a 100km-long
            self-powered adventure through Greenland’s scenic landscapes, as you
            hike across glaciers, kayak through dramatic fjords and trek along
            an ancient ice cap. You’ll visit Tasersuatsiaq Lake to gaze upon
            caribou feeding on salts from the fjord, before kayaking through
            Ikerssuaq fjord where you might spot Greenlandic seals or whales as
            you venture onwards. Being self-powered, your adventure within
            Greenland will have minimum environmental impact by design. We’ll
            also offset the carbon footprint of your flights to and from
            Greenland through{' '}
            <a
              href="https://www.clevel.co.uk/"
              target="_blank"
              rel="noopener"
              style={{ textDecoration: 'underline' }}
            >
              C Level
            </a>
            ! Check out more about how you can win this self-powered 100km
            adventure in Greenland with Much Better Adventures {''}
            <Link href="/prizes/details/holiday-greenland">
              <a
                style={{
                  textDecoration: 'underline',
                  whiteSpace: 'break-spaces',
                }}
              >
                here
              </a>
            </Link>
            .
          </p>

          <p>
            Not all travel needs to be to far-flung corners of the world. The UK
            is filled with beautiful places just waiting to be explored, without
            stepping foot on a plane to get there.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Two people climbing a rock in Trevellas Cove in Saint Agnes overlooking a sunset"
            className="singleBlogImage"
          />

          <p>
            Cornwall is renowned for being home to some of the UK’s best surfing
            beaches, beautiful coastal walks and stunning views. Our DAYMADE
            prizes include a variety of different trips to Cornwall, including a
            £500 getaway with Much Better Adventures which features luxury
            camping in the woods. You’ll spend your time pushing outside of your
            comfort zone with wild swims, rocky traverses and cliff jumps with a
            group of like-minded adventurers. Being accessible via train and
            with minimal local environmental impact, you can rest easy knowing
            your getaway isn’t costing the planet. Find out more details on our
            Cornwall adventure prize {''}
            <Link href="/prizes/details/surf-in-cornwall">
              <a
                style={{
                  textDecoration: 'underline',
                  whiteSpace: 'break-spaces',
                }}
              >
                here
              </a>
            </Link>
            .
          </p>

          <p>
            Getting away from busy schedules and going off-grid offers a unique
            experience everyone should try at least once – after all, it’s been
            quite the last 2 years… We’ve got a luxurious trip for two as part
            of our ‘staycation’ series that requires minimal travel, being based
            in the stunning Welsh countryside. Hike through stunning woodland,
            across streams and along the Welsh Mountains as you immerse yourself
            in nature once again. You’ll stay in a secluded treehouse built 30
            feet off the ground, completely off-grid yet equipped with all the
            essentials you need to enjoy your home away from home. For the more
            adventurous, try your hand at horseback riding, stand-up paddle
            boarding and canoeing, all whilst knowing you’re having minimal
            environmental impact. Winners all round. Want to win a getaway to
            the Welsh countryside? Check out more info {''}
            <Link href="/prizes/details/wales-treehouse">
              <a
                style={{
                  textDecoration: 'underline',
                  whiteSpace: 'break-spaces',
                }}
              >
                here
              </a>
            </Link>
            .
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="Brother and sister walking on a woodland path in the New Forest"
            className="singleBlogImage"
          />

          <p>
            Fancy yourself an Insta-worthy getaway in the UK? Our next
            staycation prize with minimal environmental impact takes us to
            Northern Ireland, where you’ll be staying in a premium bubble dome
            at the luxurious Finn Lough Resort. You’ll enjoy panoramic views of
            secluded Irish woodland from the comfort of your own bubble dome,
            before exploring the biodiversity on offer at the nearby lake,
            pampering yourself in the on-site spa, or trying your hand at
            morning yoga. Finn Lough maintain a strong appreciation of and
            respect for their surroundings. They employ sustainable practices to
            ensure their local impact is minimal and nature can be freely
            enjoyed by future generations for years to come. To find out how to
            win a stay in a bubble dome, read {''}
            <Link href="/prizes/details/northern-ireland">
              <a
                style={{
                  textDecoration: 'underline',
                  whiteSpace: 'break-spaces',
                }}
              >
                here
              </a>
            </Link>
            .
          </p>

          <p>
            To find out more about eco-travel solutions, you can read about how
            one of our players offsets her travel in her blog, {''}
            <a
              href="https://bea-adventurous.com/how-to-offset-your-travel-related-carbon-emissions/"
              style={{
                textDecoration: 'underline',
                whiteSpace: 'break-spaces',
              }}
            >
              Bea Adventurous
            </a>
            !
          </p>

          <p>
            Do you have any suggestions for eco-friendly travel? Let us know by
            emailing{' '}
            <a
              href="mailto:hello@daymade.co.uk"
              style={{ margin: 0, fontWeight: 'bold', color: 'inherit' }}
            >
              hello@daymade.co.uk
            </a>{' '}
            – we’re all ears!
          </p>

          <Separator style={{ margin: '1.5em 0 2em' }} />

          <p style={{ textAlign: 'center' }}>
            Enter now for your chance to win an eco-friendly travel prize:
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1.5em 0' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
